import type { ComponentProps, ReactNode } from "react";
import { Drawer as Vaul } from "vaul";
import { cn } from "../lib/cn";
import { useDocumentLocale } from "../lib/microcopy";

type Side = "bottom" | "start" | "end";

/**
 * A touch drawer (vaul) for phones: swipe to dismiss, springy snap. `side="start"` opens from the reading-start edge (right on
 * Arabic pages). Desktop side panels use `Sheet`.
 */
export function MobileDrawer({ side = "bottom", open, onOpenChange, dismissible, children }: { side?: Side; open?: boolean; onOpenChange?: (open: boolean) => void; dismissible?: boolean; children: ReactNode }) {
  const locale = useDocumentLocale();
  const direction = side === "bottom" ? "bottom" : (side === "start") === (locale !== "ar") ? "left" : "right";
  return (
    <Vaul.Root direction={direction} open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
      {children}
    </Vaul.Root>
  );
}

export const MobileDrawerTrigger = Vaul.Trigger;
export const MobileDrawerClose = Vaul.Close;

export function MobileDrawerContent({ className, children, ...props }: ComponentProps<typeof Vaul.Content>) {
  return (
    <Vaul.Portal>
      <Vaul.Overlay className="scrim fixed inset-0 z-50" />
      <Vaul.Content
        data-slot="mobile-drawer-content"
        className={cn(
          "group/drawer glass-float fixed z-50 flex h-auto flex-col outline-none",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:max-h-[85dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl data-[vaul-drawer-direction=bottom]:border-x-0 data-[vaul-drawer-direction=bottom]:border-b-0",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-[min(18rem,calc(100vw-3rem))] data-[vaul-drawer-direction=left]:border-y-0 data-[vaul-drawer-direction=left]:border-l-0",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-[min(18rem,calc(100vw-3rem))] data-[vaul-drawer-direction=right]:border-y-0 data-[vaul-drawer-direction=right]:border-r-0",
          className,
        )}
        {...props}
      >
        <div aria-hidden className="mx-auto mt-3 hidden h-1.5 w-12 shrink-0 rounded-full bg-aeblack-950/15 group-data-[vaul-drawer-direction=bottom]/drawer:block" />
        {children}
      </Vaul.Content>
    </Vaul.Portal>
  );
}

export function MobileDrawerHeader({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="mobile-drawer-header" className={cn("flex flex-col gap-1 p-4 text-start", className)} {...props} />;
}

export function MobileDrawerFooter({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="mobile-drawer-footer" className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />;
}

export function MobileDrawerTitle({ className, ...props }: ComponentProps<typeof Vaul.Title>) {
  return <Vaul.Title data-slot="mobile-drawer-title" className={cn("font-semibold text-strong", className)} {...props} />;
}

export function MobileDrawerDescription({ className, ...props }: ComponentProps<typeof Vaul.Description>) {
  return <Vaul.Description data-slot="mobile-drawer-description" className={cn("text-sm text-muted-foreground", className)} {...props} />;
}
